import { useMemo } from "react"
import { GetApp } from "../../App"
import { GraphLegend, GraphLegendDef, GraphMinMax, GraphPoint, GraphProps, GraphScales, GraphTimeseries } from "./Params"

// Coordinates are rounded to this many decimals before anything consumes them, so the
// markers and the polyline are placed from byte-identical numbers rather than two
// independently rounded values (issue #150).
const PRECISION = 3;

function round(value: number): number {
  const factor = 10 ** PRECISION;
  return Math.round(value * factor) / factor;
}

// Pure geometry for the SVG graph: turns a sorted timeseries into x/y scales and
// maps values into the fixed-height viewport. Kept free of React/DOM so it can be
// unit-tested directly; the component drives it through its useMemo hooks.
export const GraphData = {
  computeScales(timeseries: GraphTimeseries[], valueScale?: GraphMinMax): GraphScales | undefined {
    if (!timeseries) {
      return undefined;
    }
    const sorted = timeseries.map((ts) => ts.timeUtc).toSorted((a, b) => a - b);
    const valueSorted = timeseries.map((ts) => ts.value).toSorted((a, b) => a - b);
    return {
      scaleX: { max: sorted[sorted.length - 1], min: sorted[0] },
      scaleY: valueScale ? valueScale : { max: valueSorted[valueSorted.length - 1], min: valueSorted[0] },
      text: timeseries.map((t) => t.text),
      timeUtc: timeseries.map((t) => t.timeUtc),
      values: timeseries.map((t) => t.value),
    };
  },
  pointY(ts: GraphScales | undefined, value: number, height: number): number {
    if (!ts) {
      return 0;
    }
    const size = ts.scaleY.max - ts.scaleY.min;
    if (size <= 0) {
      // Every value is identical (and no explicit scale was given): put the flat line
      // through the middle rather than dividing by zero into NaN coordinates.
      return height / 2;
    }
    const vt = value - ts.scaleY.min;
    return height - (vt / size * height);
  },
  // The single source of truth for the plotted geometry: both the polyline (via `path`)
  // and the rendered markers read this array, so they cannot drift apart.
  points(ts: GraphScales | undefined, height: number): GraphPoint[] {
    if (!ts) {
      return [];
    }
    const size = ts.scaleX.max - ts.scaleX.min;
    // Anchored on the smallest timestamp rather than the first one: computeScales keeps
    // the caller's ordering, so the series is not guaranteed to be sorted.
    const x = ts.timeUtc.map((t) => (size > 0 ? round((t - ts.scaleX.min) / size * 100) : 0));
    return ts.values.map((value, i) => {
      const bandLeft = i === 0 ? 0 : round((x[i - 1] + x[i]) / 2);
      const bandRight = i === x.length - 1 ? 100 : round((x[i] + x[i + 1]) / 2);
      return {
        bandLeft: bandLeft,
        bandWidth: round(bandRight - bandLeft),
        text: ts.text[i],
        timeUtc: ts.timeUtc[i],
        value: value,
        x: x[i],
        y: round(GraphData.pointY(ts, value, height)),
      };
    });
  },
  legend(ts: GraphScales | undefined, legend: GraphLegendDef[] | undefined, height: number): GraphLegend[] | undefined {
    if (!ts) {
      return undefined;
    }
    if (legend && legend.length > 0) {
      return legend.map((l) => {
        return {
          label: l.label,
          top: GraphData.pointY(ts, l.value, height),
          value: l.value || 0,
        };
      });
    }
    const numPoints = 5;
    const size = ts.scaleY.max - ts.scaleY.min;
    return Array.from(Array(numPoints).keys()).map((n) => {
      const s = n * size / (numPoints - 1);
      const value = ts.scaleY.min + s;
      return {
        top: GraphData.pointY(ts, value, height),
        value: value || 0,
      };
    });
  },
  path(ts: GraphScales | undefined, height: number): string {
    if (!ts || ts.values.length <= 1) {
      return "";
    }
    return GraphData.points(ts, height).map((p) => `${p.x.toFixed(PRECISION)},${p.y.toFixed(PRECISION)}`).join(" ");
  },
};

export function Graph({ legend, timeseries, valueScale }: GraphProps) {
  const app = GetApp()!;
  const t = app.getT("GraphView");

  const height = 150;

  const ts = useMemo(() => {
    return GraphData.computeScales(timeseries, valueScale);
  }, [timeseries]);
  const legendY = useMemo(() => GraphData.legend(ts, legend, height), [ts]);
  const points = useMemo(() => GraphData.points(ts, height), [ts]);
  const path = useMemo(() => GraphData.path(ts, height), [ts]);

  return (timeseries && ts && legendY && ts.values.length > 1) ? (
    <div className="GraphView-Graph">
      <div className="GraphView-Graph-body">
        <div className="GraphView-Graph-scale-y">
          {legendY.map((l, i) => {
            return (
              <div key={`scale-y-${i}`} className="scale-point" style={{ top: `${l.top}px` }}>
                <div className="label">{l.label || l.value.toFixed(0)}</div>
              </div>
            );
          })}
        </div>
        {/* The plot area is sized from the same `height` the SVG viewBox uses, so a
            marker's `top` in pixels and the polyline's y in viewBox units line up 1:1. */}
        <div className="GraphView-Graph-data" style={{ height: `${height}px` }}>
          {points.map((p, i) => (
            <div key={`data-point-${i}-${p.timeUtc}`} className="data-point" style={{ left: `${p.x}%` }}>
              <div className="line-y"></div>
              <div className="point-y" style={{ top: `${p.y}px` }}></div>
            </div>
          ))}
          <svg className="line" viewBox={"0 0 100 " + height} preserveAspectRatio="none">
            <polyline className="graph-line" fill="none" points={path} stroke="currentColor" vectorEffect="non-scaling-stroke" />
          </svg>
          {/* Tooltip hit areas, centred on each point and rendered last so they sit above
              the markers and the line. */}
          {points.map((p, i) => (
            <div key={`data-point-hover-${i}-${p.timeUtc}`} className="data-point-hover" style={{ left: `${p.bandLeft}%`, width: `${p.bandWidth}%` }} title={`${p.text}`}></div>
          ))}
        </div>
      </div>
      <div className="GraphView-Graph-scale-x">
        {ts.timeUtc.map((_d, i) => (
          <div key={`scale-x-${i}`} className="t">
            {/* app.format("timeUtc", d) */}
          </div>
        ))}
      </div>
    </div>
  ) : <div className="GraphView-Graph-no-data">{t("no_data")}</div>;
}
